import type { ReactElement } from 'react'
import { RouteVariantRow, type LineDisplay } from '@metro/route-card'
import type { Route } from '@metro/routing'
import { RouteDetailsTestTags } from './testTags'
import styles from './RoutesCollectionRow.module.css'

/**
 * Pinned chip row at the top of the modal — the route-variant selector
 * (iOS `RoutesCollectionRowView`). A horizontal scroll of
 * `RouteVariantRow` chips (reused from `@metro/route-card` so the
 * "N min / N transfers / line badges" chip is byte-identical to the
 * RouteCard results carousel). Tapping a chip switches the selected
 * route; the host rebuilds the section list + fires
 * `route_details_select_route_tapped`.
 *
 * Each chip is wrapped in a `route-details-route-chip-<id>` element so
 * tests can target a specific variant (QA spec §6); the chip's own
 * `data-selected` exposes the selection state.
 */
export interface RoutesCollectionRowProps {
  readonly routes: readonly Route[]
  readonly selectedRouteId: string | null
  readonly lineLookup: ReadonlyMap<string, LineDisplay>
  readonly onSelect: (route: Route) => void
  readonly clock?: () => Date
}

export function RoutesCollectionRow({
  routes,
  selectedRouteId,
  lineLookup,
  onSelect,
  clock,
}: RoutesCollectionRowProps): ReactElement {
  return (
    <div
      className={styles.row}
      data-testid={RouteDetailsTestTags.routesCollectionRow(routes.length)}
    >
      <div className={styles.scroll} data-testid={RouteDetailsTestTags.routesCollectionScroll}>
        {routes.map((route, index) => (
          <div
            key={route.id}
            className={styles.chipSlot}
            data-testid={RouteDetailsTestTags.routeChip(route.id)}
          >
            <RouteVariantRow
              route={route}
              isSelected={route.id === selectedRouteId}
              lineLookup={lineLookup}
              onTap={() => onSelect(route)}
              clock={clock}
              variantIndex={index + 1}
              totalVariants={routes.length}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
